import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import SidebarNav from "./Sidebar";
import Topbar from "./Topbar";
import SessionBanners from "./SessionBanners";
import Assistant from "@/components/assistant/Assistant";
import { useAuth } from "@/context/AuthContext";

// Shell for all dashboards. `role` ("tester" | "admin" | "superadmin") decides the nav.
export default function DashboardLayout({ role = "tester" }) {
  const [drawer, setDrawer] = useState(false);
  const { user, refreshUser } = useAuth();
  const { pathname } = useLocation();

  // Safety net: whenever the route changes, force the mobile drawer shut and
  // release any scroll lock. Even if an exit animation ever misbehaves, a
  // navigation can never leave a full-screen scrim stuck over the page.
  useEffect(() => {
    setDrawer(false);
    document.body.style.overflow = "";
  }, [pathname]);

  // Backfill company_name for sessions cached before the field existed.
  //
  // Once per signed-in user, tracked by id. refreshUser() calls setUser with a
  // freshly-parsed object, so `user`'s identity changes and the effect re-runs — with
  // no guard that is an unbounded /auth/me loop for anyone whose company_name is
  // legitimately empty (the company settings form happily saves a blank name). Keyed
  // on the id rather than a plain boolean so a second account signed in during the
  // same tab session still gets its own attempt.
  const backfilled = useRef(null);
  useEffect(() => {
    if (!user?.company_id || user.company_name) return;
    if (backfilled.current === user.id) return;
    backfilled.current = user.id;
    refreshUser();
  }, [user, refreshUser]);

  // Lock background scroll while the mobile drawer is open, and always restore
  // it on close/unmount so a stray lock can never freeze the page.
  useEffect(() => {
    if (!drawer) return undefined;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawer]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-ink-950">
      {/* Keyboard users land on the sidebar's ~20 nav links before reaching the
          page on every single navigation without this. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[200] focus:rounded-xl focus:bg-brand-600 focus:px-4 focus:py-2.5 focus:text-sm focus:font-semibold focus:text-white focus:shadow-glow"
      >
        Skip to content
      </a>

      {/* fixed desktop sidebar — only display:none below `lg`, so it is mounted
          at the same time as the drawer. Hence the distinct `scope`. */}
      <aside className="fixed inset-y-0 left-0 hidden w-64 border-r border-slate-200 bg-white dark:border-white/10 dark:bg-ink-900 lg:block xl:w-72">
        <SidebarNav role={role} scope="desktop" />
      </aside>

      {/* Mobile drawer — ALWAYS MOUNTED, animated open/closed.
          ------------------------------------------------------------------
          This used to be an AnimatePresence with `{drawer && <motion.div/>}`
          children, and it deadlocked. When the drawer is closed by the same
          commit that changes the route — which is every single tab tap, and
          also the [pathname] effect above — framer-motion registers the exit
          but never completes it. The scrim stayed in the DOM at opacity 0 with
          pointer-events:auto: an INVISIBLE full-screen sheet over the whole
          app. One navigation worked, then every tap was swallowed until a
          reload. Verified: closing the drawer without navigating exited
          cleanly; closing it via any navigation wedged 100% of the time, and
          stayed wedged after 8s and after a forced re-render.

          Keeping both elements permanently mounted removes the dependency on
          exit-completion callbacks entirely, so there is nothing left to
          deadlock. Three independent guards make a closed drawer inert:
            - pointerEvents from LIVE state (an exiting overlay must stop
              taking input immediately anyway),
            - visibility:hidden applied after the close animation, which also
              takes the panel's links out of the tab order,
            - aria-hidden, so it is not announced when closed.
          `x: "-100%"` rather than a pixel offset: the panel is width-capped at
          85vw, so a fixed -300px left a sliver of it on screen on wider phones.
          `initial={false}` so nothing animates on first paint. */}
      <motion.div
        aria-hidden="true"
        initial={false}
        animate={drawer
          ? { opacity: 1, visibility: "visible" }
          : { opacity: 0, transitionEnd: { visibility: "hidden" } }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        onClick={() => setDrawer(false)}
        style={{ pointerEvents: drawer ? "auto" : "none", visibility: "hidden" }}
        className="fixed inset-0 z-40 bg-slate-900/60 lg:hidden"
      />
      <motion.aside
        aria-hidden={!drawer}
        initial={false}
        animate={drawer
          ? { x: "0%", visibility: "visible" }
          : { x: "-100%", transitionEnd: { visibility: "hidden" } }}
        transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
        style={{ pointerEvents: drawer ? "auto" : "none", visibility: "hidden" }}
        className="fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] flex-col border-r border-slate-200 bg-white dark:border-white/10 dark:bg-ink-900 lg:hidden"
      >
        <SidebarNav role={role} scope="drawer" onNavigate={() => setDrawer(false)} />
      </motion.aside>

      {/* content — widen the cap so large/ultrawide monitors aren't squeezed
          into a narrow centered column with big empty side margins. */}
      <div className="lg:pl-64 xl:pl-72">
        {/* Above the topbar and outside the scroll container: an impersonation
            or maintenance warning that can be scrolled away is a warning that
            gets missed. */}
        <SessionBanners role={role} />
        <Topbar role={role} onMenu={() => setDrawer(true)} />
        <main id="main-content" tabIndex={-1} className="mx-auto w-full max-w-[1700px] px-4 py-6 outline-none sm:px-6 lg:px-8 2xl:px-12 2xl:py-8">
          <Outlet />
        </main>
      </div>

      {/* AI Assistant — for testers and company admins. SuperAdmins answer
          tickets from the Support inbox instead, so they don't get the bubble. */}
      {role !== "superadmin" && <Assistant mode="app" />}
    </div>
  );
}
